// 后台统一弹窗(替换浏览器原生 alert/confirm/prompt)
//
// 原生弹窗的问题:样式跟着浏览器走(白底、"admin.vivilo.top 显示"标题)、
// 位置不可控、连着弹三个 prompt 体验极差、无法做下拉/复选等表单。
//
// 用法(命令式,返回 Promise,和原生 API 形状一致所以调用处改动最小):
//   await dialog.alert('已保存')
//   if (!(await dialog.confirm('确认删除?'))) return
//   const v = await dialog.prompt('新名称', { defaultValue: '旧值' })   // 取消返回 null
//   const r = await dialog.form({ title, fields })                      // 取消返回 null
//
// 统一:居中、暗色、Esc 关闭、点遮罩关闭、回车提交、自动聚焦首个输入。

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type FieldDef =
  | { key: string; label: string; type: 'text' | 'number'; defaultValue?: string; placeholder?: string; hint?: string }
  | { key: string; label: string; type: 'select'; options: Array<{ value: string; label: string }>; defaultValue?: string; hint?: string }
  | { key: string; label: string; type: 'checkbox'; defaultValue?: boolean; hint?: string }

type Req =
  | { kind: 'alert'; title: string; body?: string; tone?: 'info' | 'success' | 'danger'; resolve: (v: null) => void }
  | { kind: 'confirm'; title: string; body?: string; tone?: 'info' | 'danger'; confirmText?: string; resolve: (v: boolean) => void }
  | { kind: 'prompt'; title: string; body?: string; defaultValue?: string; placeholder?: string; resolve: (v: string | null) => void }
  | { kind: 'form'; title: string; body?: string; fields: FieldDef[]; confirmText?: string; resolve: (v: Record<string, string> | null) => void }

let emit: ((r: Req) => void) | null = null
const queue: Req[] = []
function push(r: Req): void {
  if (emit) emit(r)
  else queue.push(r) // Host 还没挂载(极早期调用)时先排队
}

export const dialog = {
  alert(title: string, opts: { body?: string; tone?: 'info' | 'success' | 'danger' } = {}): Promise<null> {
    return new Promise((resolve) => push({ kind: 'alert', title, ...opts, resolve }))
  },
  confirm(
    title: string,
    opts: { body?: string; tone?: 'info' | 'danger'; confirmText?: string } = {},
  ): Promise<boolean> {
    return new Promise((resolve) => push({ kind: 'confirm', title, ...opts, resolve }))
  },
  prompt(
    title: string,
    opts: { body?: string; defaultValue?: string; placeholder?: string } = {},
  ): Promise<string | null> {
    return new Promise((resolve) => push({ kind: 'prompt', title, ...opts, resolve }))
  },
  form(input: {
    title: string
    body?: string
    fields: FieldDef[]
    confirmText?: string
  }): Promise<Record<string, string> | null> {
    return new Promise((resolve) => push({ ...input, kind: 'form', resolve }))
  },
}

export function DialogHost(): JSX.Element | null {
  const [cur, setCur] = useState<Req | null>(null)
  const [pending, setPending] = useState<Req[]>([])

  useEffect(() => {
    emit = (r) => setPending((arr) => [...arr, r])
    if (queue.length > 0) {
      setPending((arr) => [...arr, ...queue.splice(0)])
    }
    return () => {
      emit = null
    }
  }, [])

  useEffect(() => {
    if (!cur && pending.length > 0) {
      setCur(pending[0]!)
      setPending((arr) => arr.slice(1))
    }
  }, [cur, pending])

  if (!cur) return null
  return <DialogView key={`${cur.kind}-${cur.title}`} req={cur} onDone={() => setCur(null)} />
}

function initialValues(req: Req): Record<string, string> {
  if (req.kind === 'prompt') return { value: req.defaultValue ?? '' }
  if (req.kind === 'form') {
    const o: Record<string, string> = {}
    for (const f of req.fields) {
      o[f.key] = f.type === 'checkbox' ? (f.defaultValue ? '1' : '') : String(f.defaultValue ?? '')
    }
    return o
  }
  return {}
}

function DialogView({ req, onDone }: { req: Req; onDone: () => void }): JSX.Element {
  const [vals, setVals] = useState<Record<string, string>>(() => initialValues(req))
  const firstRef = useRef<HTMLInputElement | HTMLSelectElement>(null)

  useEffect(() => {
    firstRef.current?.focus()
    if (firstRef.current instanceof HTMLInputElement) firstRef.current.select()
  }, [])

  function close(result: unknown): void {
    ;(req.resolve as (v: unknown) => void)(result)
    onDone()
  }
  function cancel(): void {
    close(req.kind === 'confirm' ? false : req.kind === 'alert' ? null : null)
  }
  function submit(): void {
    if (req.kind === 'confirm') close(true)
    else if (req.kind === 'prompt') close(vals.value ?? '')
    else if (req.kind === 'form') close(vals)
    else close(null)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancel()
      // textarea 之外回车即提交
      if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement)) submit()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const danger = 'tone' in req && req.tone === 'danger'
  const success = 'tone' in req && req.tone === 'success'
  const accent = danger ? 'var(--danger, #ff6b6b)' : success ? 'var(--success, #10d49a)' : 'var(--brand, #22e3ff)'

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={cancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.6)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px, 100%)',
          background: 'var(--bg-2, #12161f)',
          border: '1px solid var(--border, rgba(255,255,255,.1))',
          borderTop: `2px solid ${accent}`,
          borderRadius: 14,
          padding: 24,
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: req.body || req.kind !== 'alert' ? 10 : 18, whiteSpace: 'pre-line' }}>
          {req.title}
        </div>
        {req.body && (
          <div className="text-sm text-muted" style={{ marginBottom: 16, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {req.body}
          </div>
        )}

        {req.kind === 'prompt' && (
          <input
            ref={firstRef as React.RefObject<HTMLInputElement>}
            className="input"
            style={{ width: '100%', marginBottom: 18 }}
            value={vals.value ?? ''}
            placeholder={req.placeholder}
            onChange={(e) => setVals({ value: e.target.value })}
          />
        )}

        {req.kind === 'form' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
            {req.fields.map((f, i) => (
              <div key={f.key}>
                {f.type === 'checkbox' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={vals[f.key] === '1'}
                      onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.checked ? '1' : '' }))}
                    />
                    {f.label}
                  </label>
                ) : (
                  <>
                    <div className="label" style={{ fontSize: 11.5, marginBottom: 5 }}>{f.label}</div>
                    {f.type === 'select' ? (
                      <select
                        ref={i === 0 ? (firstRef as React.RefObject<HTMLSelectElement>) : undefined}
                        className="select"
                        style={{ width: '100%' }}
                        value={vals[f.key] ?? ''}
                        onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        ref={i === 0 ? (firstRef as React.RefObject<HTMLInputElement>) : undefined}
                        className="input"
                        type={f.type === 'number' ? 'number' : 'text'}
                        style={{ width: '100%' }}
                        value={vals[f.key] ?? ''}
                        placeholder={f.placeholder}
                        onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                      />
                    )}
                  </>
                )}
                {f.hint && (
                  <div className="text-sm text-muted" style={{ fontSize: 11, marginTop: 4 }}>{f.hint}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {req.kind !== 'alert' && (
            <button className="btn" onClick={cancel}>取消</button>
          )}
          <button
            className={danger ? 'btn danger' : 'btn primary'}
            onClick={submit}
            style={{ minWidth: 88 }}
          >
            {req.kind === 'alert' ? '知道了' : ('confirmText' in req && req.confirmText) || '确定'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
